const isSuccess = response =>
  response && response.status >= 200 && response.status < 300;

const getError = response => {
  let errors = [{ message: 'An unknown error occurred' }];
  if (response?.data?.errors && Array.isArray(response?.data?.errors)) {
    errors = response.data.errors;
  } else if (response && response.data) {
    errors = [response.data];
  }
  return { errors, ok: false, data: null, statusCode: response?.status };
};

const getSuccess = response => ({
  data: response.data,
  errors: [],
  ok: true,
});

const dataProvider = axiosWrapper => ({
  getApiUrl: () => axiosWrapper.defaults.baseURL,
  getMany: async resource => {
    try {
      const response = await axiosWrapper.get(resource);
      return response?.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  getOne: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  deleteOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.delete(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  createOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  updateOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  postApi: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  getApi: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  putApi: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  get: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  getBlob: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
        responseType: 'blob',
      });
      if (isSuccess(response)) {
        return {
          ...response,
          ok: true,
        };
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  post: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);

      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  put: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  patch: async (resource, data) => {
    try {
      const response = await axiosWrapper.patch(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  remove: async (resource, data) => {
    try {
      const response = await axiosWrapper.delete(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  stream: async (resource, data, { signal } = {}) => {
    const baseURL = axiosWrapper.defaults.baseURL || '';
    const path = resource.startsWith('/') ? resource.slice(1) : resource;
    const url = `${baseURL.replace(/\/$/, '')}/${path}`;
    const config = {
      url: resource,
      method: 'post',
      data,
      headers: {
        ...axiosWrapper.defaults.headers?.common,
        'Content-Type': 'application/json',
      },
    };
    const handlers = axiosWrapper.interceptors.request.handlers || [];
    const resolvedConfig = await handlers.reduce(
      (promise, h) =>
        promise.then(c =>
          typeof h.fulfilled === 'function'
            ? Promise.resolve(h.fulfilled(c))
            : c,
        ),
      Promise.resolve(config),
    );
    return fetch(url, {
      method: 'POST',
      headers: resolvedConfig.headers,
      body: JSON.stringify(data),
      signal,
    });
  },
  custom: () => Promise.resolve({ data: null }),
});

export default dataProvider;
